import i18next from 'i18next';
import type { PlannerRegion } from '../../types';
import { DEFAULT_PLANNER_REGION } from './seasonality';

export type HomeLocationMode = 'idle' | 'locating' | 'granted' | 'manual' | 'denied';

export interface HomeCoordinates {
  lat: number;
  lon: number;
}

export interface HomeLocationState {
  mode: HomeLocationMode;
  region: PlannerRegion | null;
  source: 'geolocation' | 'manual' | null;
  coordinates: HomeCoordinates | null;
}

export interface ResolvedHomeLocation {
  region: PlannerRegion;
  coordinates: HomeCoordinates;
}

const STORAGE_KEY = 'home.locationAwarePreview';

const VALID_REGIONS: PlannerRegion[] = [
  'tyrrhenian',
  'adriatic',
  'ionian',
  'sicily_channel',
  'western_med',
  'aegean_cyprus'
];

const REGION_REPRESENTATIVE_COORDINATES: Record<PlannerRegion, HomeCoordinates> = {
  tyrrhenian: { lat: 41.1200, lon: 12.4500 },
  adriatic: { lat: 42.4500, lon: 14.2000 },
  ionian: { lat: 39.9500, lon: 17.9500 },
  sicily_channel: { lat: 36.1500, lon: 13.2000 },
  western_med: { lat: 39.3500, lon: 3.1000 },
  aegean_cyprus: { lat: 35.1500, lon: 27.8000 }
};

const clampRegion = (value: unknown): PlannerRegion | null => {
  if (typeof value === 'string' && VALID_REGIONS.includes(value as PlannerRegion)) {
    return value as PlannerRegion;
  }

  return null;
};

const clampCoordinates = (value: unknown): HomeCoordinates | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const maybe = value as Partial<HomeCoordinates>;
  if (typeof maybe.lat !== 'number' || typeof maybe.lon !== 'number') {
    return null;
  }

  return { lat: maybe.lat, lon: maybe.lon };
};

export const getRegionRepresentativeCoordinates = (region: PlannerRegion): HomeCoordinates => {
  return REGION_REPRESENTATIVE_COORDINATES[region] || REGION_REPRESENTATIVE_COORDINATES[DEFAULT_PLANNER_REGION];
};

export const getStoredHomeLocationState = (): HomeLocationState => {
  if (typeof window === 'undefined') {
    return { mode: 'idle', region: null, source: null, coordinates: null };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { mode: 'idle', region: null, source: null, coordinates: null };
    }

    const parsed = JSON.parse(raw) as Partial<HomeLocationState>;
    return {
      mode: parsed.mode === 'granted' || parsed.mode === 'manual' || parsed.mode === 'denied' ? parsed.mode : 'idle',
      region: clampRegion(parsed.region),
      source: parsed.source === 'geolocation' || parsed.source === 'manual' ? parsed.source : null,
      coordinates: clampCoordinates(parsed.coordinates)
    };
  } catch {
    return { mode: 'idle', region: null, source: null, coordinates: null };
  }
};

export const storeHomeLocationState = (state: HomeLocationState): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const clearHomeLocationState = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(STORAGE_KEY);
};

export const inferPlannerRegionFromCoordinates = (lat: number, lon: number): PlannerRegion => {
  if (lon >= 19.5) {
    return 'aegean_cyprus';
  }

  if (lon >= 14.5 && lat < 37.6) {
    return 'sicily_channel';
  }

  if (lon >= 15.2 && lat >= 37.6) {
    return 'ionian';
  }

  if (lon >= 12.2 && lat >= 40.8) {
    return 'adriatic';
  }

  if (lon <= 8.4) {
    return 'western_med';
  }

  if (lat < 36.8 && lon < 16.5) {
    return 'sicily_channel';
  }

  if (lat >= 38.2 && lon < 15.2) {
    return 'tyrrhenian';
  }

  return DEFAULT_PLANNER_REGION;
};

export const requestUserHomeLocation = async (): Promise<ResolvedHomeLocation> => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error(String(i18next.t('codex:locationAwareHome.geolocationNotSupported')));
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coordinates = {
          lat: position.coords.latitude,
          lon: position.coords.longitude
        };

        resolve({
          region: inferPlannerRegionFromCoordinates(coordinates.lat, coordinates.lon),
          coordinates
        });
      },
      (error) => {
        reject(new Error(error.message || String(i18next.t('codex:locationAwareHome.locationUnavailable'))));
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 1000 * 60 * 60 * 12
      }
    );
  });
};

export const requestUserRegion = async (): Promise<PlannerRegion> => {
  const resolved = await requestUserHomeLocation();
  return resolved.region;
};
